// Мета-теги страницы при переходах внутри SPA (fd-web-03).
//
// Первую загрузку размечает сервер (src/index.ts на стенде, шлюз через Caddy на
// проде): title, description, canonical, OG и JSON-LD уже в HTML. При переходах
// SPA сервер не участвует — хук берёт те же данные у шлюза (`GET /seo/page`) и
// переписывает теги в <head>. Старый адрес товара `/product/:id` хук заменяет
// на ЧПУ (как 301 на сервере), не перезагружая страницу.
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router";

import { api, type SeoPage } from "@/api";

const DEFAULT_TITLE = "OSMIX — маркетплейс изделий ручной работы";
const DEFAULT_DESCRIPTION =
	"OSMIX — маркетплейс изделий ручной работы: украшения, керамика, текстиль и декор от независимых мастеров.";

const upsertMeta = (attr: "name" | "property", key: string, content: string | undefined) => {
	let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
	if (!content) {
		el?.remove();
		return;
	}
	if (!el) {
		el = document.createElement("meta");
		el.setAttribute(attr, key);
		document.head.appendChild(el);
	}
	el.setAttribute("content", content);
};

const setCanonical = (href: string | undefined) => {
	let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
	if (!href) {
		el?.remove();
		return;
	}
	if (!el) {
		el = document.createElement("link");
		el.rel = "canonical";
		document.head.appendChild(el);
	}
	el.href = href;
};

const setJsonLd = (items: SeoPage["jsonLd"]) => {
	for (const el of Array.from(document.head.querySelectorAll('script[type="application/ld+json"]'))) el.remove();
	for (const item of items) {
		const el = document.createElement("script");
		el.type = "application/ld+json";
		el.textContent = JSON.stringify(item);
		document.head.appendChild(el);
	}
};

export const applyPageMeta = (meta: SeoPage) => {
	document.title = meta.title;
	upsertMeta("name", "description", meta.description);
	upsertMeta("name", "robots", meta.robots);
	setCanonical(meta.canonical);
	upsertMeta("property", "og:title", meta.title);
	upsertMeta("property", "og:description", meta.description);
	upsertMeta("property", "og:url", meta.canonical);
	upsertMeta("property", "og:image", meta.image);
	upsertMeta("name", "twitter:title", meta.title);
	upsertMeta("name", "twitter:description", meta.description);
	upsertMeta("name", "twitter:image", meta.image);
	setJsonLd(meta.jsonLd);
};

/** Вернуть общие теги витрины, когда страница с собственными тегами закрыта. */
export const resetPageMeta = () => {
	document.title = DEFAULT_TITLE;
	upsertMeta("name", "description", DEFAULT_DESCRIPTION);
	upsertMeta("name", "robots", undefined);
	setCanonical(undefined);
	for (const key of ["og:title", "og:description", "og:url", "og:image"]) upsertMeta("property", key, undefined);
	for (const key of ["twitter:title", "twitter:description", "twitter:image"]) upsertMeta("name", key, undefined);
	setJsonLd([]);
};

/**
 * Обновить title, description, canonical, OG и JSON-LD по текущему адресу.
 * Вызывается одной строкой на индексируемых страницах (товар, магазин, каталог,
 * идея, главная, «Все идеи»).
 */
export function usePageMeta() {
	const location = useLocation();
	const navigate = useNavigate();
	const path = `${location.pathname}${location.search}`;
	const meta = useQuery({
		queryKey: ["seo", "page", path],
		queryFn: () => api.seo.page(path),
		staleTime: 5 * 60_000,
		retry: false,
	});
	const data = meta.data;

	useEffect(() => {
		if (!data || data.path !== path) return;
		if (data.status === 301 && data.redirect && data.redirect !== path) {
			navigate(`${data.redirect}${location.hash}`, { replace: true });
			return;
		}
		applyPageMeta(data);
	}, [data, path, navigate, location.hash]);

	useEffect(() => resetPageMeta, []);
}
